<template>
  <div class="checkout-page">
    <!-- Header -->
    <div class="checkout-header">
      <div class="header-content">
        <span class="logo">收银台</span>
      </div>
    </div>

    <!-- Main Content -->
    <div class="checkout-content">
      <!-- Order Information -->
      <div class="order-info-section">
        <div class="order-info-left">
          <div class="order-info-item">
            <span class="order-label">订单描述:</span>
            <span class="order-value">{{ orderDescription }}</span>
          </div>
          <div class="order-info-item">
            <span class="order-label">订单类型:</span>
            <span class="order-value">{{ orderType }}</span>
          </div>
          <!-- Bill details (only shown in bill mode) -->
          <template v-if="mode === 'bill' && billAmount > 0">
            <div class="order-info-item">
              <span class="order-label">账单金额:</span>
              <span class="order-value">¥{{ billAmount.toFixed(2) }}</span>
            </div>
            <div class="order-info-item coupon-row">
              <span class="order-label">优惠券:</span>
              <span class="order-value coupon-value">
                <template v-if="!hasUsableCoupons">
                  ¥0.00
                  <span class="coupon-tip">暂无可用优惠券</span>
                </template>
                <template v-else>
                  <span v-if="selectedCouponId">-¥{{ couponAmount.toFixed(2) }}</span>
                  <span v-else>未选择</span>
                  <span class="coupon-tip">{{ usableCoupons.length }}张可用</span>
                  <a-button type="link" size="small" @click="openCouponDialog" v-if="!paymentConfirmed">
                    {{ selectedCouponId ? '更换优惠券' : '选择优惠券' }}
                  </a-button>
                  <a-button
                    v-if="selectedCouponId && !paymentConfirmed"
                    type="link"
                    size="small"
                    @click="clearCoupon">
                    取消使用
                  </a-button>
                </template>
              </span>
            </div>
            <div class="order-info-item">
              <span class="order-label">账户余额:</span>
              <span class="order-value">¥{{ accountBalance.toFixed(2) }}</span>
            </div>
          </template>
        </div>
        <div class="order-info-right">
          <span class="amount-label">应付金额:</span>
          <span class="amount-value">¥{{ payableAmount.toFixed(2) }}</span>
        </div>
        <div class="order-info-right" v-if="needRecharge">
          <span class="amount-label">实付金额:</span>
          <span class="amount-value">¥{{ rechargeAmount.toFixed(2) }}</span>
        </div>
      </div>

      <!-- Payment Method Tabs -->
      <div class="payment-tabs" v-if="needRecharge && !paymentConfirmed">
        <div
          class="payment-tab payment-tab-wechat"
          :class="{ 'tab-active': payType === 'tenpay' }"
          @click="clickPayType('tenpay')">
          <div class="tab-icon-wrap">
            <a-icon type="wechat" class="tab-icon" />
          </div>
          <div class="tab-text">
            <div class="tab-title">微信支付</div>
            <div class="tab-desc">推荐使用微信扫码支付</div>
          </div>
          <a-icon v-if="payType === 'tenpay'" type="check-circle" theme="filled" class="tab-check" />
        </div>
        <div
          class="payment-tab payment-tab-alipay"
          :class="{ 'tab-active': payType === 'alipay' }"
          @click="clickPayType('alipay')">
          <div class="tab-icon-wrap">
            <a-icon type="alipay" class="tab-icon" />
          </div>
          <div class="tab-text">
            <div class="tab-title">支付宝</div>
            <div class="tab-desc">跳转支付宝完成支付</div>
          </div>
          <a-icon v-if="payType === 'alipay'" type="check-circle" theme="filled" class="tab-check" />
        </div>
      </div>

      <div class="payment-content-direct" v-if="directPayBill">
        <a-button type="primary" @click="handleBalancePay">
          {{ $t('scope.pay') }}
        </a-button>
      </div>

      <div class="payment-content-direct" v-if="!directPayBill && !paymentConfirmed">
        <a-button type="primary" @click="handleConfirmPayment" :disabled="!payType">
          {{ $t('scope.confirm_pay') }}
        </a-button>
      </div>

      <!-- Payment Content -->
      <div class="payment-content" v-if="payType && needRecharge && paymentConfirmed">
        <div class="payment-method-header">
          <a-icon
            v-if="payType === 'tenpay'"
            type="wechat"
            class="wechat-logo" />
          <icon
            v-else-if="payType === 'alipay'"
            type="alipay"
            class="wechat-logo" />
          <span class="payment-method-name">
            {{ payType === 'tenpay' ? $t('scope.wechat_pay') : $t('scope.alipay') }}
          </span>
        </div>

        <!-- Tenpay: QR Code -->
        <template v-if="qrCodeData">
          <div class="qr-code-section">
            <div class="qr-code-wrapper">
              <vue-qrcode :value="qrCodeData" :options="{ width: 300 }" />
            </div>
            <div class="qr-code-tip">
              <a-icon type="scan" class="scan-icon" />
              <span>请扫描二维码以完成支付</span>
            </div>
          </div>
        </template>

        <!-- Alipay: HTML form / redirect -->
        <template v-else-if="htmlContent">
          <div class="loading-section">
            <a-spin size="large" />
            <p>已在新窗口打开支付宝，请在新窗口完成支付</p>
            <a-button type="link" @click="submitPaymentHtml(htmlContent)">重新打开支付窗口</a-button>
          </div>
        </template>

        <!-- Loading State -->
        <div class="loading-section" v-if="loading && !qrCodeData && !htmlContent">
          <a-spin size="large" />
          <p>正在生成支付信息...</p>
        </div>

        <!-- Error State -->
        <div class="error-section" v-else-if="error && !loading">
          <a-alert :message="error" type="error" show-icon />
          <a-button type="primary" @click="handleRetry" class="retry-btn">重试</a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import WindowsMixin from '@/mixins/windows'

export default {
  name: 'Checkout',
  components: {
    VueQrcode,
  },
  mixins: [WindowsMixin],
  data () {
    return {
      payType: '', // tenpay or alipay
      loading: false,
      error: null,
      qrCodeData: null, // 二维码数据（tenpay）
      htmlContent: null, // HTML form / 页面（主要用于 alipay）
      paymentId: null,
      pollTimer: null,
      // Order info
      orderDescription: '',
      orderType: '',
      payableAmount: 0,
      // Mode: 'recharge' or 'bill'
      mode: 'recharge',
      chargeAmount: null,
      billId: null,
      // Bill details
      accountBalance: 0,
      billAmount: 0,
      couponAmount: 0,
      usableCoupons: [],
      selectedCouponId: null,
      paymentConfirmed: false,
      from: null,
    }
  },
  computed: {
    ...mapState('app', {
      customer: state => state.customer,
    }),
    needRecharge () {
      return this.rechargeAmount > 0
    },
    rechargeAmount () {
      if (this.mode === 'recharge') {
        return this.payableAmount
      }
      if (this.mode === 'bill') {
        return Math.max(0, this.payableAmount - this.accountBalance)
      }
      return 0
    },
    directPayBill () {
      return this.mode === 'bill' && !this.needRecharge
    },
    hasUsableCoupons () {
      return !!this.selectedCouponId || (Array.isArray(this.usableCoupons) && this.usableCoupons.length > 0)
    },
  },
  created () {
    this.initParams()
  },
  beforeDestroy () {
    this.clearPollTimer()
  },
  methods: {
    async requestPayment (amount, payType, billId, orderDescription) {
      if (!amount || !payType) return null

      const paymentM = new this.$Manager('billing_payments')
      let paymentId = this.paymentId

      // 如果还没有 paymentId，先创建 payment
      if (!paymentId) {
        const extra = {}
        if (billId) {
          extra.bill_id = billId
        }
        if (this.selectedCouponId) {
          extra.coupons = [this.selectedCouponId]
        }
        extra.return_url = this.getReturnUrl()
        const createPayload = {
          type: payType,
          amount,
          extra,
          description: orderDescription,
        }
        if (billId) {
          createPayload.bill_id = billId
        }
        const { data } = await paymentM.create({ data: createPayload })
        paymentId = data && data.id
        this.paymentId = paymentId
      }

      // 对已有 payment 直接 request，获取具体支付内容
      const { data: reqData } = await paymentM.performAction({
        id: paymentId,
        action: 'request',
        data: {
          method: 'native',
          description: orderDescription || this.orderDescription,
        },
      })
      console.log('requestPayment', reqData)
      return reqData || null
    },
    async fetchBillInfo (billId, couponId) {
      try {
        const billM = new this.$Manager('billing_bills')
        const params = {}
        if (couponId) {
          params.coupons = [couponId]
        }
        const resp = await billM.getSpecific({
          id: billId,
          spec: 'pay',
          params,
        })
        const info = resp.data || {}
        this.billAmount = Number(info.amount) || 0
        this.accountBalance = Number(info.balance) || 0
        this.payableAmount = Number(info.pay_amount) || 0
        this.couponAmount = Number(info.coupon_amount) || 0
        this.selectedCouponId = couponId || null
        this.usableCoupons = info.coupons || []
        this.mode = 'bill'
        this.billId = billId
        this.orderType = '账单支付'
        this.orderDescription = `${this.orderType} (${this.billId}) ${this.payableAmount}元`
      } catch (error) {
        console.error('fetchBillInfo error:', error)
        this.error = error.response?.data?.message || '获取账单信息失败，请重试'
      }
    },
    resetPaymentState () {
      this.paymentConfirmed = false
      this.paymentId = null
      this.qrCodeData = null
      this.htmlContent = null
      this.error = null
      this.clearPollTimer()
    },
    openCouponDialog () {
      if (!this.hasUsableCoupons) return
      this.createDialog('SelectCouponDialog', {
        coupons: this.usableCoupons,
        selectedCouponId: this.selectedCouponId,
        onSelect: async (coupon) => {
          await this.applyCoupon(coupon ? coupon.id : null)
        },
      })
    },
    async clearCoupon () {
      await this.applyCoupon(null)
    },
    async applyCoupon (couponId) {
      if (!this.billId) return
      this.resetPaymentState()
      this.loading = true
      try {
        await this.fetchBillInfo(this.billId, couponId)
      } finally {
        this.loading = false
      }
    },
    async handleConfirmPayment () {
      this.paymentConfirmed = true
      await this.handleSelectPayType(this.payType)
    },
    async fetchPaymentInfo (paymentId) {
      try {
        const paymentM = new this.$Manager('billing_payments')
        const { data } = await paymentM.get({
          id: paymentId,
        })
        this.chargeAmount = data.amount
        this.payableAmount = data.amount
        this.orderDescription = data.description || this.orderDescription
        this.payType = data.type
        this.paymentId = data.id
        this.mode = 'recharge'
        this.orderType = '账户充值'
        this.orderDescription = `${this.orderType} ${this.payableAmount}元`
      } catch (error) {
        console.error('fetchPaymentInfo error:', error)
        this.error = error.response?.data?.message || '获取支付信息失败，请重试'
      }
    },
    async initParams () {
      console.log('initParams', this.$route.query)
      const { charge_amount, bill_id, payment_id, from } = this.$route.query
      this.from = from
      if (bill_id) {
        this.paymentConfirmed = false
        await this.fetchBillInfo(bill_id)
      } else if (payment_id) {
        this.paymentConfirmed = true
        await this.fetchPaymentInfo(payment_id)
      } else if (charge_amount) {
        this.paymentConfirmed = true
        this.mode = 'recharge'
        const amount = Number(charge_amount) || 0
        this.chargeAmount = amount
        this.payableAmount = amount
        this.orderType = '账户充值'
        this.orderDescription = `${this.orderType} ${this.payableAmount}元`
      } else {
        this.error = '缺少必要参数：charge_amount 或 bill_id 或 payment_id'
      }
      // 已有支付单时直接进入支付流程
      // 没有可用优惠券也直接进入支付流程
      if (this.needRecharge && this.payType && this.paymentConfirmed) {
        this.$nextTick(() => {
          this.handleSelectPayType(this.payType)
        })
      }
    },
    clickPayType (type) {
      if (this.paymentConfirmed) {
        return
      }
      this.payType = type
    },
    async handleSelectPayType (type) {
      this.payType = type
      this.error = null
      this.qrCodeData = null
      this.htmlContent = null
      this.clearPollTimer()

      if (!this.rechargeAmount || this.rechargeAmount <= 0) {
        this.error = '实付金额为 0, 无需支付'
        return
      }

      // 异步请求前先打开窗口，避免弹窗被浏览器拦截
      let paymentWindow = null
      if (type === 'alipay') {
        paymentWindow = window.open('about:blank', '_blank')
      }

      this.loading = true
      try {
        const resp = await this.requestPayment(this.rechargeAmount, this.payType, this.billId, this.orderDescription)
        if (!resp) {
          if (paymentWindow) paymentWindow.close()
          this.error = '未获取到支付链接，请重试'
        } else if (resp.type === 'url') {
          if (paymentWindow) paymentWindow.close()
          // URL 场景：tenpay 用二维码，alipay 直接跳转
          this.qrCodeData = resp.message
          this.startPolling()
        } else if (resp.type === 'html') {
          // 在新窗口执行支付宝返回的自动提交表单 HTML
          this.htmlContent = resp.message
          this.startPolling()
          this.submitPaymentHtml(resp.message, paymentWindow)
        } else {
          if (paymentWindow) paymentWindow.close()
          this.error = '未获取到支付链接，请重试'
        }
      } catch (error) {
        if (paymentWindow) paymentWindow.close()
        console.error('Create payment error:', error)
        this.error = error.response?.data?.message || '创建支付订单失败，请重试'
      } finally {
        this.loading = false
      }
    },
    handleRetry () {
      if (this.payType) {
        this.handleSelectPayType(this.payType)
      }
    },
    normalizePaymentHtml (html) {
      let content = String(html || '').trim()
      if ((content.startsWith('"') && content.endsWith('"')) || (content.startsWith("'") && content.endsWith("'"))) {
        content = content.slice(1, -1)
      }
      return content
    },
    submitPaymentHtml (html, win) {
      const content = this.normalizePaymentHtml(html)
      if (!content) return

      let paymentWin = win
      if (!paymentWin || paymentWin.closed) {
        paymentWin = window.open('about:blank', '_blank')
      }
      if (!paymentWin) {
        this.error = '请允许浏览器弹出窗口以完成支付'
        return
      }

      paymentWin.document.open()
      paymentWin.document.write(content)
      paymentWin.document.close()
      try {
        paymentWin.focus()
      } catch (e) {
        // ignore
      }
    },
    getReturnUrl () {
      var url = window.location.href
      var urlObj = new URL(url)
      var returnUrl = urlObj.protocol + '//' + urlObj.host
      if (this.from) {
        if (this.from === 'billing') {
          returnUrl += '/account/billing'
        } else if (this.from === 'tech-support') {
          returnUrl += '/account/tech-support'
        } else {
          returnUrl += '/account'
        }
      } else {
        returnUrl += '/account'
      }
      return returnUrl
    },
    onPaymentSuccess () {
      setTimeout(() => {
        if (this.from) {
          if (this.from === 'billing') {
            this.$router.push('/account/billing')
          } else if (this.from === 'tech-support') {
            this.$router.push('/account/tech-support')
          } else {
            this.$router.push('/account')
          }
        } else {
          this.$router.push('/account')
        }
      }, 500)
    },
    startPolling () {
      this.clearPollTimer()
      if (!this.paymentId) return

      this.pollTimer = setInterval(async () => {
        try {
          const paymentM = new this.$Manager('billing_payments')
          const { data: { data = [] } } = await paymentM.list({
            params: { id: this.paymentId },
          })
          const item = data[0]
          if (!item) return
          console.log('startPolling', item)
          if (item.status === 'complete' || item.status === 'paid' || item.status === 'success') {
            this.clearPollTimer()
            this.$message.success('支付成功！')
            this.onPaymentSuccess()
          } else if (item.status === 'failed' || item.status === 'canceled' || item.status === 'fail') {
            this.clearPollTimer()
            this.error = '支付失败或已取消'
            this.$message.error('支付失败或已取消')
          }
        } catch (error) {
          console.error('Poll payment status error:', error)
          // 网络异常时不立刻停止轮询
        }
      }, 3000)
    },
    async handleBalancePay () {
      if (!this.billId) {
        this.$message.error('缺少账单信息，无法支付')
        return
      }
      if (this.accountBalance < this.payableAmount) {
        this.$message.warning('账户余额不足，请选择支付通道')
        return
      }
      this.loading = true
      try {
        const billM = new this.$Manager('billing_bills')
        const data = {}
        if (this.selectedCouponId) {
          data.coupons = [this.selectedCouponId]
        }
        await billM.performAction({
          id: this.billId,
          action: 'pay',
          data,
        })
        this.$message.success('支付成功！')
        this.onPaymentSuccess()
      } catch (error) {
        console.error('handleBalancePay error', error)
        this.$message.error(error.response?.data?.message || '支付失败，请重试')
      } finally {
        this.loading = false
      }
    },
    clearPollTimer () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
  },
}
</script>

<style lang="less" scoped>
.checkout-page {
  min-height: 100vh;
  // background-color: #f5f5f5;
}

.checkout-header {
  // background-color: #f0f0f0;
  padding: 20px 40px;
  border-bottom: 1px solid #e8e8e8;

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;

    .logo {
      font-size: 24px;
      font-weight: bold;
      color: #000;
    }

    .header-title {
      font-size: 16px;
      color: #000;
    }
  }
}

.checkout-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.order-info-section {
  background-color: #fff;
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .order-info-left {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .order-info-item {
      display: flex;
      gap: 8px;

      .order-label {
        color: #666;
        font-size: 14px;
      }

      .order-value {
        color: #000;
        font-size: 14px;
        font-weight: 500;
      }

      &.coupon-row {
        align-items: center;

        .coupon-value {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px;
        }

        .coupon-tip {
          color: #999;
          font-size: 12px;
          font-weight: 400;
          margin-left: 4px;
        }
      }
    }
  }

  .order-info-right {
    display: flex;
    align-items: baseline;
    gap: 8px;

    .amount-label {
      color: #666;
      font-size: 14px;
    }

    .amount-value {
      color: #ff4d4f;
      font-size: 24px;
      font-weight: bold;
    }
  }
}

.payment-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  .payment-tab {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    cursor: pointer;
    position: relative;
    background: #fff;
    border: 1.5px solid #e8e8e8;
    border-radius: 10px;
    transition: all 0.25s ease;
    overflow: hidden;

    .tab-icon-wrap {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f5;
      flex-shrink: 0;
      transition: all 0.25s ease;

      .tab-icon {
        font-size: 24px;
        color: #8c8c8c;
        line-height: 1;
      }
    }

    .tab-text {
      flex: 1;
      min-width: 0;
      text-align: left;

      .tab-title {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        line-height: 1.3;
      }

      .tab-desc {
        margin-top: 4px;
        font-size: 12px;
        color: #8c8c8c;
        line-height: 1.4;
      }
    }

    .tab-check {
      position: absolute;
      top: 12px;
      right: 12px;
      font-size: 18px;
    }

    &:hover:not(.disabled):not(.tab-active) {
      border-color: #bfbfbf;
      background: #fafafa;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    }

    &.disabled {
      color: #ccc;
      cursor: not-allowed;
      opacity: 0.6;
    }

    &.tab-active {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);

      .tab-title {
        font-weight: 700;
      }

      .tab-desc {
        color: inherit;
        opacity: 0.75;
      }
    }

    &.payment-tab-wechat.tab-active {
      border-color: #07c160;
      background: linear-gradient(135deg, #f0fff6 0%, #e8f9ef 100%);
      color: #07c160;

      .tab-icon-wrap {
        background: rgba(7, 193, 96, 0.12);

        .tab-icon {
          color: #07c160;
        }
      }

      .tab-title {
        color: #07c160;
      }

      .tab-check {
        color: #07c160;
      }
    }

    &.payment-tab-alipay.tab-active {
      border-color: #1677ff;
      background: linear-gradient(135deg, #f0f7ff 0%, #e8f1ff 100%);
      color: #1677ff;

      .tab-icon-wrap {
        background: rgba(22, 119, 255, 0.12);

        .tab-icon {
          color: #1677ff;
        }
      }

      .tab-title {
        color: #1677ff;
      }

      .tab-check {
        color: #1677ff;
      }
    }
  }
}

.payment-content {
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 40px;
  min-height: 400px;

  .payment-method-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e8e8e8;

    .wechat-logo {
      font-size: 32px;
      color: #07c160;
    }

    .payment-method-name {
      font-size: 18px;
      font-weight: 500;
      color: #000;
    }
  }

  .qr-code-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    .qr-code-wrapper {
      width: 300px;
      height: 300px;
      border: 1px solid #e8e8e8;
      border-radius: 4px;
      padding: 20px;
      background-color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;

      .qr-code-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .qr-code-tip {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      background-color: #1890ff;
      color: #fff;
      border-radius: 4px;
      font-size: 14px;

      .scan-icon {
        font-size: 18px;
      }
    }
  }

  .loading-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 300px;

    p {
      color: #666;
      font-size: 14px;
    }
  }

  .error-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    min-height: 300px;
    justify-content: center;

    .retry-btn {
      margin-top: 16px;
    }
  }
}

.payment-content-direct {
  display: flex;
  justify-content: right;
  align-items: right;
  min-height: 200px;
}
</style>
